import { UserRole } from 'api/graphql-global-types';
// Types
import { GetStream_stream } from 'api/streams/types/GetStream';
import {
  GetMyCampaignOfferByCampaignId_getMyCampaignOfferByCampaignId_submissions_CampaignLiveAppearanceSubmission_stream,
  GetMyCampaignOfferByCampaignId_getMyCampaignOfferByCampaignId_submissions_CampaignRecordedAppearanceSubmission_draftStream,
} from 'api/campaignV2/types/GetMyCampaignOfferByCampaignId';
import { MySoonestStream_myStreams_entities } from 'api/streams/types/MySoonestStream';
import {
  GetStoreBySlug_store_hashtags,
  GetStoreBySlug_store_nextStream,
} from 'api/store/types/GetStoreBySlug';
import { MyStreams_myStreams_entities } from 'api/streams/types/MyStreams';
import { AmaRequest_amaRequest_store_nextStream } from 'api/ama/types/AmaRequest';
import {
  GetAllHomePageData_getHomePageData_merch,
  GetAllHomePageData_getHomePageData_watchStreams,
} from 'api/homePage/types/GetAllHomePageData';
import { GetUpcomingStreams_streams_entities } from 'api/streams/types/GetUpcomingStreams';
import { GetStreams_streamsV2_entities } from 'api/streams/types/GetStreams';
import { GetMerchProducts_getMerchProducts_entities } from 'api/merch/types/GetMerchProducts';
import { MyOutcomingStreamOrders_myOutcomingStreamOrders_entities_stream } from 'api/streams/types/MyOutcomingStreamOrders';
import { HomepageSearch_streamsV2_entities } from 'api/search/types/HomepageSearch';
import { GetAllMerchProducts_getMerchProducts_entities } from 'api/allMerch/types/GetAllMerchProducts';
import { GetMemorabiliaBySlug_getMemorabiliaBySlug } from 'api/memorabilia/types/GetMemorabiliaBySlug';
import { GetExperienceBySlug_getExperienceBySlug } from 'api/experiences/types/GetExperienceBySlug';
import { GetMerchMinimalInfo_getMerchProducts_entities } from 'api/merch/types/GetMerchMinimalInfo';
import { SoonestUpcomingInterview_soonestUpcomingInterview_entities_stream } from 'api/interview/types/SoonestUpcomingInterview';
// Constants
import {
  ACCOUNT_SETTINGS,
  EVENT_ANALYTICS,
  EXPERIENCES,
  MEMORABILIA,
  MERCH,
  TAG,
} from 'constants/routes';

export type StreamComputeParams =
  | GetStream_stream
  | GetStoreBySlug_store_nextStream
  | MyStreams_myStreams_entities
  | GetUpcomingStreams_streams_entities
  | GetStreams_streamsV2_entities
  | AmaRequest_amaRequest_store_nextStream
  | MyOutcomingStreamOrders_myOutcomingStreamOrders_entities_stream
  | HomepageSearch_streamsV2_entities
  | MySoonestStream_myStreams_entities
  | GetMyCampaignOfferByCampaignId_getMyCampaignOfferByCampaignId_submissions_CampaignLiveAppearanceSubmission_stream
  | GetMyCampaignOfferByCampaignId_getMyCampaignOfferByCampaignId_submissions_CampaignRecordedAppearanceSubmission_draftStream
  | SoonestUpcomingInterview_soonestUpcomingInterview_entities_stream
  | GetAllHomePageData_getHomePageData_watchStreams
  | null
  | undefined;

/**
 * Only the two slugs are needed to build a stream path, so this takes the
 * structural minimum rather than StreamComputeParams — that union is also used
 * by callers that read purchase state, and widening it for path-building alone
 * breaks them.
 */
type StreamPathParams =
  | {
      slug?: string | null;
      store?: { slug?: string | null } | null;
    }
  | null
  | undefined;

export const getStreamPath = (
  stream: StreamPathParams,
  path:
    | 'streams'
    | 'start-stream'
    | 'cohost-stream'
    | 'moderate-stream'
    | 'stream-settings'
    | 'watch-stream/video'
): string => {
  const storeSlug = stream?.store?.slug;
  const streamSlug = stream?.slug;

  if (!storeSlug || !streamSlug) {
    return '/';
  }

  return `/${storeSlug}/${path}/${streamSlug}`;
};

export const computeOrderStreamPath = (stream: StreamPathParams): string => {
  return getStreamPath(stream, 'streams');
};

export const computeStartStreamPath = (stream: StreamPathParams): string => {
  return getStreamPath(stream, 'start-stream');
};

export const computeEventAnalyticsPath = (ticketEventId: string): string =>
  `${EVENT_ANALYTICS}/${ticketEventId}`;

export const computeCohostStartStreamPath = (
  stream: StreamPathParams
): string => {
  return getStreamPath(stream, 'cohost-stream');
};

export const computeVideoWatchStreamPath = (
  stream: StreamComputeParams
): string => {
  return getStreamPath(stream, 'watch-stream/video');
};

export const computeStreamAnalyticsPath = (streamId: string): string =>
  `/manage-streams/${streamId}/analytics`;

export const getTalentProfileStreamsUrl = (
  stream: StreamComputeParams
): string => {
  return `/${stream?.store?.slug}/streams`;
};

export type MerchProductPathParams = {
  store?: { slug?: string | null } | null;
  slug: string;
};

export const computeMerchProductPath = (
  merchItem:
    | MerchProductPathParams
    | GetAllHomePageData_getHomePageData_merch
    | GetMerchProducts_getMerchProducts_entities
    | GetAllMerchProducts_getMerchProducts_entities
    | GetMerchMinimalInfo_getMerchProducts_entities
): string => {
  return `/${merchItem.store?.slug}${MERCH}/${merchItem.slug}`;
};

export const computeMemorabiliaProductPath = (
  memorabiliaItem: GetMemorabiliaBySlug_getMemorabiliaBySlug | undefined
): string => {
  if (!memorabiliaItem) return '/';

  return `/${memorabiliaItem.store?.slug}/memorabilia/${memorabiliaItem.slug}`;
};

export const computeExperiencePath = (
  experience: GetExperienceBySlug_getExperienceBySlug | undefined
): string => {
  if (!experience) return '/';

  return `/${experience.store?.slug}/experience/${experience.slug}`;
};

export const computeTagPath = (tag: GetStoreBySlug_store_hashtags): string => {
  return `${TAG}/${tag.name.toLowerCase()}`;
};

export const computeShopPath = (store: {
  slug: string | null;
  hasMerch?: boolean;
  hasMemorabilia?: boolean;
  hasPureProducts?: boolean;
  hasExperiences?: boolean;
}): string => {
  if (!store.slug) return `${MERCH}?athleteIds=unknown`;

  const basePath = `/${store.slug}`;

  // "Products" now show alongside memorabilia there.
  if (store.hasMemorabilia || store.hasPureProducts)
    return `${basePath}${MEMORABILIA}`;
  if (store.hasMerch) return `${basePath}${MERCH}`;
  if (store.hasExperiences) return `${basePath}${EXPERIENCES}`;

  // When user has no products, return to their profile home page instead of general merch page
  return basePath;
};

export const isActiveRoute = (
  routePathname: string,
  routeUrl: string,
  itemQuery: string | null = null,
  query: string | string[] | null = null
): boolean => {
  if (query && itemQuery) {
    return query.includes(itemQuery);
  } else {
    return routePathname === routeUrl;
  }
};

/**
 * Matches a route and everything nested under it. Used by nav entries that own
 * more than one top-level route (e.g. My Shop owns /manage-merch and
 * /account-products).
 */
export const isActiveRouteSection = (
  routePathname: string,
  sectionUrls: string[]
): boolean =>
  sectionUrls.some(
    (sectionUrl) =>
      routePathname === sectionUrl ||
      routePathname.startsWith(`${sectionUrl}/`) ||
      routePathname.startsWith(`${sectionUrl}?`)
  );

export const cleanSlug = (slug: string | string[] | undefined) => {
  const slugString = Array.isArray(slug) ? slug[0] : slug;
  return slugString?.split('?')[0];
};

export const getEnvLink = (): string => {
  return process.env.NODE_ENV === 'development'
    ? 'http://localhost:4000'
    : process.env.NEXT_PUBLIC_BASE_PATH || '';
};

export const getCBEnvLink = (): string => {
  return process.env.NODE_ENV === 'development'
    ? 'http://localhost:4200'
    : process.env.NEXT_PUBLIC_CB_BASE_PATH || '';
};

export const getHostCalendarTab = (role?: UserRole | null): number => {
  return role === UserRole.Organization ? 5 : 7;
};

export const sanitizeRedirectUrlPath = (
  redirectUrl?: string | string[] | null
): string | null => {
  const redirectValue = Array.isArray(redirectUrl)
    ? redirectUrl[0]
    : redirectUrl;

  if (!redirectValue) {
    return null;
  }

  const trimmedRedirectValue = redirectValue.trim();

  if (
    !trimmedRedirectValue ||
    /^(?:[a-z]+:)?\/\//i.test(trimmedRedirectValue) ||
    trimmedRedirectValue.startsWith('\\')
  ) {
    return null;
  }

  const normalizedRedirectValue = trimmedRedirectValue.replace(/^[/\\]+/, '');

  return normalizedRedirectValue || null;
};

export const computeAccountSettingsHostCalendarPath = (
  role?: UserRole | null,
  redirectUrl?: string
): string => {
  const searchParams = new URLSearchParams({
    tab: String(getHostCalendarTab(role)),
  });
  const sanitizedRedirectUrl = sanitizeRedirectUrlPath(redirectUrl);

  if (sanitizedRedirectUrl) {
    searchParams.set('redirectUrl', sanitizedRedirectUrl);
  }

  return `${ACCOUNT_SETTINGS}?${searchParams.toString()}`;
};
